import { useCallback, useEffect, useState } from "react";

import type { BoardDropdownOption } from "@/components/QuickSearch/components/BoardDropdown/models/boardDropdownOption";
import type { BasePolyOption } from "@/components/QuickSearch/components/DropdownList/components/RenderMenu";

export enum BoardOptionType {
  Board,
  IsLoading,
  NoBoards,
}

export type PolymorphicBoardOption = BasePolyOption & {
  type: BoardOptionType;
  board?: BoardDropdownPolyOption;
};

export type BoardDropdownPolyOption = BoardDropdownOption & {
  onClick?: (item: PolymorphicBoardOption) => void;
};

export const usePolyBoardOptions = (
  options: BoardDropdownOption[],
  selectedValue: number | null,
  isLoading: boolean,
  noItems: boolean,
  boardHeight: number,
  onChange: (board: BoardDropdownOption) => void
) => {
  const [polyBoards, setPolyBoards] = useState<PolymorphicBoardOption[]>([]);

  const onClick = useCallback(
    (polyBoard: PolymorphicBoardOption) => {
      onChange(polyBoard.board as BoardDropdownOption);
    },
    [onChange]
  );

  const createPolyItems = useCallback(() => {
    if (noItems) {
      const polyNoBoards: PolymorphicBoardOption = {
        type: BoardOptionType.NoBoards,
        height: boardHeight,
      };

      setPolyBoards([polyNoBoards]);
      return;
    }

    const res: PolymorphicBoardOption[] = [];

    for (const board of options) {
      res.push({
        type: BoardOptionType.Board,
        height: boardHeight,
        board: {
          ...board,
          isSelected: selectedValue === board.value,
          onClick,
        },
      });
    }

    if (isLoading) {
      const loadingPolyBoard: PolymorphicBoardOption = {
        type: BoardOptionType.IsLoading,
        height: boardHeight,
      };
      res.push(loadingPolyBoard);
    }

    setPolyBoards(res);
  }, [boardHeight, isLoading, noItems, onClick, options, selectedValue]);

  useEffect(() => {
    createPolyItems();
  }, [createPolyItems]);

  return polyBoards;
};
